import type React from 'react';
import { graphql } from 'react-apollo';

import { compact } from 'lodash';
import { branch, compose, renderComponent, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';
import connectToRouter from 'js/lib/connectToRouter';

import { NotFound } from 'bundles/common/components/withNotFound';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';
import { constructLanguageMap } from 'bundles/common/utils/subtitleUtils';
import type Track from 'bundles/interactive-transcript/models/Track';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';
import { buildTracks, getDefaultTrack } from 'bundles/interactive-transcript/utils/TrackUtils';
import type {
  OnDemandLectureVideosV1,
  OnDemandLectureVideosV1GetQuery as OnDemandLectureVideosV1GetQueryData,
  OnDemandLectureVideosV1GetQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/OnDemandLectureVideosV1';
import type { OnDemandVideosV1 } from 'bundles/naptimejs/resources/__generated__/OnDemandVideosV1';
import MetaImageQuery, { MetaImageProductType } from 'bundles/sharing-common/utils/MetaImageQuery';
import type { MetaImageQueryData, MetaImageQueryVariables } from 'bundles/sharing-common/utils/MetaImageQuery';
import { InternalServerError, NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { getVideoTranscript } from 'bundles/video-logged-out-page/actions/getVideoTranscript';
import type {
  CourseDetailsQueryVlp as CourseDetailsQueryVlpData,
  CourseDetailsQueryVlpVariables,
  CourseDetailsQueryVlp_OnDemandCoursesV1Resource_slug_elements as OnDemandCoursesV1ResourceData,
  CourseDetailsQueryVlp_XdpV1Resource_slug_elements as XdpApiData,
  CourseDetailsQueryVlp_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember as XdpCourseMetadata,
} from 'bundles/video-logged-out-page/components/__generated__/CourseDetailsQueryVlp';
import type {
  FetchProductOverviewQueryVlp as FetchProductOverviewQueryVlpData,
  FetchProductOverviewQueryVlpVariables,
} from 'bundles/video-logged-out-page/components/__generated__/FetchProductOverviewQueryVlp';
import { SESSION_STORAGE_AUTO_PLAY_VIDEO_ITEM } from 'bundles/video-logged-out-page/constants';
import { CourseDetailsQueryVlp } from 'bundles/video-logged-out-page/queries/CourseDetailsQueryVlp';
import { FetchProductOverviewQueryVlp } from 'bundles/video-logged-out-page/queries/FetchProductOverviewQueryVlp';
import { LectureVideosVLPQuery } from 'bundles/video-logged-out-page/queries/LectureVideosVLPQuery';
import { VLPCourseMaterialsQuery } from 'bundles/video-logged-out-page/queries/VLPCourseMaterialsQuery';
import type {
  VLPCourseMaterialsQuery_OnDemandLearnerMaterialsV1Resource_courses_elements_learnerMaterialItems_lectures as Item,
  VLPCourseMaterialsQuery_OnDemandLearnerMaterialsV1Resource_courses_elements_learnerMaterialLessons_elements as Lesson,
  VLPCourseMaterialsQuery as VLPCourseMaterialsQueryData,
  VLPCourseMaterialsQueryVariables,
  VLPCourseMaterialsQuery_OnDemandLearnerMaterialsV1Resource_courses_elements_learnerMaterialModules_elements as VLPModule,
} from 'bundles/video-logged-out-page/queries/__generated__/VLPCourseMaterialsQuery';
import type { SessionStorageAutoplayItem } from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import {
  extractItemSlugAndId,
  extractVideoLectureItems,
  getCurrentLessonFromItem,
  getCurrentModuleFromLesson,
  getNextVideoItem,
  getVideoItem,
} from 'bundles/video-logged-out-page/utils/utils';
import { toSeconds } from 'bundles/video-trim/utils/videoTrimmingUtils';

type NetworkError = (Error & { statusCode: number }) | null | undefined;

type PropsFromRouter = {
  pathname: string;
  courseSlug: string;
  itemId: string;
  autoPlayVideo: boolean;
  startAt: number | undefined;
  isSFCContent: boolean;
};

type VLPCourse = {
  id: string;
  slug: string;
  name: string;
  description: string;
  instructorIds: string[];
  partnerIds: number[];
};

type PropsFromCourseDetailsQuery = {
  xdpApiData: XdpApiData | null | undefined;
  onDemandCourse: OnDemandCoursesV1ResourceData | null | undefined;
  course: VLPCourse | undefined;
  courseId: string | undefined;
  csrError: boolean;
  courseDetailsQueryDone: boolean;
};

type PropsFromFetchProductOverviewsQuery = {
  fetchProductOverviewQueryDone: boolean;
  reviewOverview: unknown | undefined;
};

type PropsFromVLPCourseMaterialsQuery = {
  currentModule: VLPModule | undefined;
  currentLesson: Lesson | undefined;
  currentVideoItem: Item | undefined;
  nextVideoItem: Item;
  lectureItems: Item[];
  courseMaterialsQueryDone: boolean;
  csrError: boolean;
};

type PropsFromGqlLoading = {
  gqlLoading: boolean;
};

type PropsFromLectureVideosVLPQuery = {
  video: OnDemandLectureVideosV1 | undefined;
  onDemandVideos: OnDemandVideosV1 | undefined;
  gqlLoading: boolean;
};

type PropsGivenToLectureVideosVLPQuery = PropsFromRouter &
  PropsFromCourseDetailsQuery &
  PropsFromFetchProductOverviewsQuery &
  PropsFromVLPCourseMaterialsQuery &
  PropsFromGqlLoading;

type PropsFromLectureVideosVLPQueryWithVideoDefined = Omit<PropsFromLectureVideosVLPQuery, 'video'> & {
  video: OnDemandLectureVideosV1;
};

type PropsFromMetaImageQuery = {
  metaImageSrc: string;
};

type PropsFromGetTracksWithProps = {
  tracks: TrackList;
  // getDefaultTrack isn't typed anywhere and fixing that is out of scope for this PR, so I left this as any
  defaultTrack: any;
};

type PropsGivenToDefaultTrackBranch = PropsGivenToLectureVideosVLPQuery &
  PropsFromLectureVideosVLPQueryWithVideoDefined &
  PropsFromMetaImageQuery &
  PropsFromGetTracksWithProps;

type PropsFromTranscriptConnectToStores = {
  track: Track;
};

type PropsToComponent = PropsFromRouter &
  PropsFromCourseDetailsQuery &
  PropsFromFetchProductOverviewsQuery &
  PropsFromVLPCourseMaterialsQuery &
  PropsFromGqlLoading &
  PropsFromLectureVideosVLPQuery;

// since VLPLoggedOutPage requires complex queries and logic to fetch info, separating this out so the component is more readable
export default function (WrappedComponent: React.ComponentType<PropsToComponent>) {
  return compose<PropsToComponent, {}>(
    connectToRouter(({ location: { pathname, query }, params: { courseSlug, itemSlugAndId } }) => {
      const splitItemSlugAndId = extractItemSlugAndId(itemSlugAndId);

      if (!splitItemSlugAndId) {
        throw new NotFoundError();
      }
      const itemId = splitItemSlugAndId?.[1];

      let autoPlayVideo = false;
      if (!inServerContext && sessionStorageEx.isAvailable()) {
        const { autoPlayVideo: jsonAutoPlayVideo } = sessionStorageEx.getItem<Partial<SessionStorageAutoplayItem>>(
          SESSION_STORAGE_AUTO_PLAY_VIDEO_ITEM,
          JSON.parse,
          {},
          {},
          { autoPlayVideo: false }
        );

        if (jsonAutoPlayVideo) {
          autoPlayVideo = jsonAutoPlayVideo;
          // remove so if user refreshes we won't try to autoplay the video and cause an error (autoplay doesn't work if user hasn't interacted with the page)
          // if user waits for the next video to play after the current one ends, we'll set this item again (in NextVideoCounter) before we CSR the next video page
          sessionStorageEx.removeItem(SESSION_STORAGE_AUTO_PLAY_VIDEO_ITEM);
        }
      }

      return {
        pathname,
        courseSlug,
        itemId,
        autoPlayVideo,
        startAt: Number(query?.startAt) || undefined,
      };
    }),
    graphql<PropsFromRouter, CourseDetailsQueryVlpData, CourseDetailsQueryVlpVariables, PropsFromCourseDetailsQuery>(
      CourseDetailsQueryVlp,
      {
        props: ({ data, ownProps: { pathname } }) => {
          const xdpApiData = data?.XdpV1Resource?.slug?.elements?.[0];
          const loading = data?.loading;
          const error = data?.error;
          const onDemandCourse = data?.OnDemandCoursesV1Resource?.slug?.elements?.[0];

          let csrError = false;

          if (!loading && !xdpApiData) {
            const networkError = error?.networkError as NetworkError;
            if (networkError?.statusCode === 500) {
              throw new InternalServerError(pathname);
            } else if (inServerContext) {
              throw new NotFoundError();
            } else {
              csrError = true;
            }
          }

          const xdpMetadata = xdpApiData?.xdpMetadata as XdpCourseMetadata;

          let course;
          if (xdpMetadata?.cdpMetadata) {
            const { cdpMetadata } = xdpMetadata;
            course = {
              id: cdpMetadata.id,
              slug: cdpMetadata.slug,
              name: cdpMetadata.name,
              description: cdpMetadata.description,
              // For some reason, the graphql autogenerator is not adding launchedAt to the generated query
              // @ts-expect-error Property 'launchedAt' does not exist on type 'CourseDetailsQueryVlp_OnDemandCoursesV1Resource_slug_elements'.ts(2339)
              startDate: onDemandCourse?.launchedAt,
              instructorIds: cdpMetadata.instructors.map((instructor) => instructor.id),
              partnerIds: cdpMetadata.partners.map((partner) => partner.id),
            };
          }

          return {
            xdpApiData,
            onDemandCourse,
            course,
            courseId: course?.id,
            courseDetailsQueryDone: !loading,
            csrError,
          };
        },
      }
    ),
    graphql<
      PropsGivenToLectureVideosVLPQuery,
      OnDemandLectureVideosV1GetQueryData,
      OnDemandLectureVideosV1GetQueryVariables,
      PropsFromLectureVideosVLPQuery
    >(LectureVideosVLPQuery, {
      skip: ({ csrError, xdpApiData }) => !csrError && !(xdpApiData?.xdpMetadata as XdpCourseMetadata)?.cdpMetadata?.id,
      options: ({ itemId, xdpApiData }) => {
        const xdpMetadata = xdpApiData?.xdpMetadata as XdpCourseMetadata;
        const courseId = xdpMetadata?.cdpMetadata?.id;

        return {
          variables: {
            id: courseId + '~' + itemId,
          },
        };
      },
      props: ({ data, ownProps: { gqlLoading } }) => {
        const loading = data?.loading;

        const lectureVideos = data?.OnDemandLectureVideosV1?.get;
        const video = lectureVideos?.elements?.[0];

        // TODO: figure out why naptime graphql generator doesn't generate the types for linked query
        // @ts-expect-error Property 'linked' does not exist on type 'OnDemandLectureVideosV1Connection'.ts(2339)
        const onDemandVideos = lectureVideos?.linked?.onDemandVideosV1?.[0] as OnDemandVideosV1 | undefined;

        if (!loading && !video && inServerContext) {
          // only in server context because throwing this on client creates a breaking error
          throw new NotFoundError();
        }

        return {
          video,
          onDemandVideos,
          gqlLoading: !!(gqlLoading || loading),
        };
      },
    }),
    graphql<
      PropsFromRouter & PropsFromCourseDetailsQuery,
      FetchProductOverviewQueryVlpData,
      FetchProductOverviewQueryVlpVariables,
      PropsFromFetchProductOverviewsQuery
    >(FetchProductOverviewQueryVlp, {
      props: ({ data }) => {
        const loading = data?.loading;
        // separating this here because this api is flaky and often takes down the entire XDP query
        // tried updating error policy but it broke on prod, this is duct tape while we investigate error policy issues
        const reviewOverview = data?.XdpV1Resource?.slug?.elements?.[0]?.fetchProductOverview?.elements?.[0];
        return {
          fetchProductOverviewQueryDone: !loading,
          reviewOverview,
        };
      },
    }),
    graphql<
      PropsFromRouter & PropsFromCourseDetailsQuery & PropsFromFetchProductOverviewsQuery,
      VLPCourseMaterialsQueryData,
      VLPCourseMaterialsQueryVariables,
      PropsFromVLPCourseMaterialsQuery
    >(VLPCourseMaterialsQuery, {
      skip: ({ courseId }) => !courseId,
      props: ({ data, ownProps: { itemId, courseId, csrError, pathname } }) => {
        const loading = data?.loading;
        const error = data?.error;
        const onDemandLearnerMaterials = data?.OnDemandLearnerMaterialsV1Resource?.courses?.elements?.[0];

        const modules = compact(onDemandLearnerMaterials?.learnerMaterialModules?.elements);
        const items = compact(onDemandLearnerMaterials?.learnerMaterialItems?.lectures);
        const lessons = compact(onDemandLearnerMaterials?.learnerMaterialLessons?.elements);

        const currentLesson = getCurrentLessonFromItem(lessons, itemId);
        const lessonId = currentLesson?.id;
        const currentModule = getCurrentModuleFromLesson(modules, lessonId);
        const lectureItems = extractVideoLectureItems(items);
        const currentVideoItem = getVideoItem(items, itemId);
        const nextVideoItem = lectureItems && getNextVideoItem(lectureItems, itemId);

        // since this gets skipped, if we have the courseId (the data this query needs before running) and loading is false, this completed
        let queryDone = false;
        let queryCsrError = false;
        const hasFetchedData = lectureItems && currentVideoItem && nextVideoItem;

        if (!loading && courseId) {
          queryDone = true;
          const networkError = error?.networkError as NetworkError;

          if (networkError?.statusCode === 500) {
            throw new InternalServerError(pathname);
          } else if (!hasFetchedData) {
            if (inServerContext) {
              throw new NotFoundError();
            } else {
              queryCsrError = true;
            }
          }
        }
        return {
          currentModule,
          currentLesson,
          currentVideoItem,
          nextVideoItem,
          lectureItems,
          courseMaterialsQueryDone: queryDone,
          csrError: csrError || queryCsrError,
        };
      },
    }),
    withProps(
      (
        props: PropsFromRouter &
          PropsFromCourseDetailsQuery &
          PropsFromFetchProductOverviewsQuery &
          PropsFromVLPCourseMaterialsQuery
      ) => {
        const { courseMaterialsQueryDone, courseDetailsQueryDone, fetchProductOverviewQueryDone } = props;
        let allQueriesLoading = true;
        // detect if all queries are done
        // this isn't ideal, but works until we create a dedicated resource for this page
        if (courseMaterialsQueryDone && courseDetailsQueryDone && fetchProductOverviewQueryDone) {
          allQueriesLoading = false;
        }

        return { gqlLoading: allQueriesLoading };
      }
    ),
    branch(
      ({ video }: PropsGivenToLectureVideosVLPQuery & PropsFromLectureVideosVLPQuery) => !!video,
      compose<
        PropsToComponent,
        // At this point, the video prop is defined because of the previous branch call
        PropsGivenToLectureVideosVLPQuery & PropsFromLectureVideosVLPQueryWithVideoDefined & PropsFromMetaImageQuery
      >(
        graphql<
          PropsGivenToLectureVideosVLPQuery & PropsFromLectureVideosVLPQueryWithVideoDefined,
          MetaImageQueryData,
          MetaImageQueryVariables,
          PropsFromMetaImageQuery
        >(MetaImageQuery, {
          options: ({ video }) => {
            return {
              variables: {
                // The naptime type generator typed videoId as an object with an id field, but in reality it's a string.
                // I force it to a string here
                productId: video.videoId as unknown as string,
                metaImageProductType: MetaImageProductType.VLP,
              },
            };
          },
          props: ({ data }) => {
            return {
              metaImageSrc: data?.MetaImagesV1Resource?.elements?.[0]?.url ?? '',
            };
          },
        }),
        withProps<
          PropsFromGetTracksWithProps,
          PropsGivenToLectureVideosVLPQuery & PropsFromLectureVideosVLPQueryWithVideoDefined & PropsFromMetaImageQuery
        >(({ onDemandVideos }) => {
          const languageMap = onDemandVideos
            ? constructLanguageMap(onDemandVideos.subtitlesVtt, onDemandVideos.subtitlesAssetTags)
            : {};
          const tracks = buildTracks(languageMap);

          return {
            tracks,
            defaultTrack: getDefaultTrack(tracks, 'en'),
          };
        }),
        branch<PropsGivenToDefaultTrackBranch>(
          ({ defaultTrack }) => defaultTrack,
          compose<PropsToComponent, PropsGivenToDefaultTrackBranch>(
            connectToFluxibleContext(
              (
                { executeAction },
                { defaultTrack, video }: PropsFromGetTracksWithProps & Pick<PropsFromLectureVideosVLPQuery, 'video'>
              ) => {
                const trimRange =
                  video?.startMs && video?.endMs
                    ? {
                        trimmedStartTimeSeconds: toSeconds(video.startMs),
                        trimmedEndTimeSeconds: toSeconds(video.endMs),
                      }
                    : undefined;
                executeAction(getVideoTranscript, { track: defaultTrack, trimRange });
              }
            ),
            connectToStores<
              PropsGivenToDefaultTrackBranch & PropsFromGetTracksWithProps,
              PropsGivenToDefaultTrackBranch & PropsFromGetTracksWithProps & PropsFromTranscriptConnectToStores
            >(['VideoTranscriptStore'], (context, props) => {
              const trackData = context.VideoTranscriptStore.getData() as Track;
              return {
                ...props,
                track: trackData,
              };
            })
          )
        )
      )
    ),
    branch(({ csrError }: { csrError: boolean }) => csrError, renderComponent(NotFound))
  )(WrappedComponent);
}
